import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { programContentSlidePresenceKey } from '../services/programContentSlideService';
import type { ProgramProgressCell } from '../types/domain';
import type { TrackProgramMode } from './trackProgramModeStorage';
import {
  resolveLearningMemoNoteIndicatorVisible,
  resolvePresentationIndicatorVisible,
  resolveWorksheetIndicatorVisible,
  resolveIndicatorDraftAndCellTexts,
} from './learningAttachmentIndicatorResolve';
import { appendPresentationRefOwnerFromLearningText } from './programContentSlideOwner';
import {
  resolveSyncMirrorLearningTextForIndicators,
  resolveSyncMirrorSlideOwnerCandidatesForIndicator,
  resolveWorksheetDraftStorageRowKey,
} from './programSyncMirrorIndicators';
import { cellLearningTextForEditor } from './programLearningContentFields';
import type { ProgramProgressRow } from './programProgressRows';

export type AnnualProgressLearningIndicatorRow = {
  annualCellKey: string;
  slideLearningTextRaw: string;
  slideOwnerCandidates: ProgramContentSlideOwnerRef[];
  isPresentationSyncMirror?: boolean;
  /** 프로그램 표 scopeId — 있으면 다른 편제 로컬 초안 잔상 차단 */
  slideWorkspaceScopeId?: string;
};

export type AnnualSyncMirrorIndicatorSourceRow = AnnualProgressLearningIndicatorRow & {
  seq: number;
  templateLabel: string;
  programKindLabel: string;
  scopeLookupAlias: string;
  slideOpenDbOwner: ProgramContentSlideOwnerRef | null;
};

export type AnnualSyncMirrorIndicatorMaps = {
  repAnnualCellKeyByMirrorKey: Record<string, string>;
  repIndicatorCellsByMirrorKey: Record<string, ProgramProgressCell>;
  repSlideOwnerCandidatesByMirrorKey: Record<string, ProgramContentSlideOwnerRef[]>;
};

export type ProgramLearningIndicatorRowContext = {
  annualKey: string;
  row: ProgramProgressRow;
  cell?: ProgramProgressCell;
  patchAnnualKey?: string;
  draftLearningText?: string | null;
  cellLearningText: string;
  /** 표시·인디케이터 공통 canonical learning_text — 있으면 ref·note 해석에 우선 */
  canonicalLearningText?: string;
};

function repAnnualRowToIndicatorCell(
  repRow: AnnualSyncMirrorIndicatorSourceRow,
  programCellsByKey: Record<string, ProgramProgressCell>
): ProgramProgressCell {
  const pc = programCellsByKey[repRow.annualCellKey];
  const owner = repRow.slideOpenDbOwner;
  const ownerId = owner?.id?.trim() ?? pc?.id?.trim() ?? '';
  if (pc) {
    return {
      ...pc,
      id: ownerId || pc.id,
      learning_text: repRow.slideLearningTextRaw,
      learning_note: pc.learning_note,
      presentation_ref_id: pc.presentation_ref_id,
      worksheet_ref_id: pc.worksheet_ref_id,
    };
  }
  return {
    id: ownerId,
    workspace_id: '',
    grade_id: '',
    academic_year: 0,
    term_number: 1,
    monday_iso: '',
    weekday_key: 'mon',
    slot_index: 0,
    timetable_text: '',
    unit_text: '',
    learning_text: repRow.slideLearningTextRaw,
    page_text: '',
    assistant_page_text: '',
    current_period_text: '',
    total_period_text: '',
    material_text: '',
    record_cross_curricular_text: '',
    record_memo_text: '',
    record_evaluation_text: '',
    record_class_text: '',
    record_member_group_id: null,
    bg_color_by_field: {},
  };
}

/** 연간진도표: 동기화 편제 미러 행 → 대표 행 매핑(프로그램 표와 동일하게 occurrence 순서 정렬) */
export function buildAnnualSyncMirrorIndicatorMaps(
  rows: AnnualSyncMirrorIndicatorSourceRow[],
  trackProgramModeByTrack: Record<string, TrackProgramMode>,
  programCellsByKey: Record<string, ProgramProgressCell>
): AnnualSyncMirrorIndicatorMaps {
  const repAnnualCellKeyByMirrorKey: Record<string, string> = {};
  const repIndicatorCellsByMirrorKey: Record<string, ProgramProgressCell> = {};
  const repSlideOwnerCandidatesByMirrorKey: Record<string, ProgramContentSlideOwnerRef[]> = {};

  const repByTemplate = new Map<string, AnnualSyncMirrorIndicatorSourceRow[]>();
  const mirrorByTemplateAlias = new Map<string, Map<string, AnnualSyncMirrorIndicatorSourceRow[]>>();

  for (const row of rows) {
    const template = row.templateLabel.trim();
    if (!template || row.programKindLabel === '프로') continue;
    if ((trackProgramModeByTrack[template] ?? 'unified') !== 'sync') continue;
    if (row.isPresentationSyncMirror) {
      const alias = row.scopeLookupAlias.trim();
      if (!alias) continue;
      const aliasMap = mirrorByTemplateAlias.get(template) ?? new Map<string, AnnualSyncMirrorIndicatorSourceRow[]>();
      const arr = aliasMap.get(alias) ?? [];
      arr.push(row);
      aliasMap.set(alias, arr);
      mirrorByTemplateAlias.set(template, aliasMap);
    } else {
      const arr = repByTemplate.get(template) ?? [];
      arr.push(row);
      repByTemplate.set(template, arr);
    }
  }

  for (const [template, aliasMap] of Array.from(mirrorByTemplateAlias.entries())) {
    const rep = (repByTemplate.get(template) ?? []).sort(
      (a: AnnualSyncMirrorIndicatorSourceRow, b: AnnualSyncMirrorIndicatorSourceRow) => a.seq - b.seq
    );
    Array.from(aliasMap.values()).forEach((mirrorRows) => {
      const mirror = [...mirrorRows].sort(
        (a: AnnualSyncMirrorIndicatorSourceRow, b: AnnualSyncMirrorIndicatorSourceRow) => a.seq - b.seq
      );
      mirror.forEach((mirrorRow: AnnualSyncMirrorIndicatorSourceRow, index: number) => {
        const repRow = rep[index];
        const mirrorKey = mirrorRow.annualCellKey.trim();
        const repKey = repRow?.annualCellKey.trim();
        if (!mirrorKey || !repKey) return;
        repAnnualCellKeyByMirrorKey[mirrorKey] = repKey;
        repIndicatorCellsByMirrorKey[mirrorKey] = repAnnualRowToIndicatorCell(repRow, programCellsByKey);
        repSlideOwnerCandidatesByMirrorKey[mirrorKey] = [...repRow.slideOwnerCandidates];
      });
    });
  }

  return {
    repAnnualCellKeyByMirrorKey,
    repIndicatorCellsByMirrorKey,
    repSlideOwnerCandidatesByMirrorKey,
  };
}

function resolveAnnualLearningIndicatorInputs(
  row: AnnualProgressLearningIndicatorRow,
  learningTextOverlayByAnnualKey: Record<string, string>,
  syncMaps: AnnualSyncMirrorIndicatorMaps
): {
  patchAnnualKey: string;
  draftLearningText: string;
  cellLearningText: string;
  indicatorLearningText: string;
  worksheetDraftRowKey: string;
  slideOwnerCandidates: ProgramContentSlideOwnerRef[];
} {
  const annualKey = row.annualCellKey.trim();
  const syncMirrorView = !!row.isPresentationSyncMirror;
  const patchAnnualKey = resolveWorksheetDraftStorageRowKey(
    annualKey,
    syncMirrorView,
    syncMaps.repAnnualCellKeyByMirrorKey
  );
  const repCell = syncMirrorView ? syncMaps.repIndicatorCellsByMirrorKey[annualKey] : undefined;
  const cellLearningText = repCell
    ? cellLearningTextForEditor(repCell)
    : cellLearningTextForEditor({ learning_text: row.slideLearningTextRaw });
  const overlayDraft =
    learningTextOverlayByAnnualKey[patchAnnualKey] ?? learningTextOverlayByAnnualKey[annualKey];
  const { draftLt, cellLt } = resolveIndicatorDraftAndCellTexts({
    annualKey,
    patchAnnualKey,
    /**
     * overlay가 없는 셀은 draft를 빈 문자열로 강제하지 않는다.
     * (빈 문자열 강제 시 기존 DB 텍스트까지 "로컬 편집 중"으로 오판해 표시/인디케이터가 꺼질 수 있음)
     */
    draftLearningText: overlayDraft,
    cellLearningText,
    lsOverlayByAnnualKey: learningTextOverlayByAnnualKey,
  });
  const repDraftLearningText = learningTextOverlayByAnnualKey[patchAnnualKey] ?? '';
  const indicatorLearningText = resolveSyncMirrorLearningTextForIndicators(
    annualKey,
    syncMirrorView,
    syncMaps.repIndicatorCellsByMirrorKey,
    draftLt,
    cellLt,
    repDraftLearningText
  );
  const slideOwnerCandidates =
    syncMirrorView && syncMaps.repSlideOwnerCandidatesByMirrorKey[annualKey]?.length
      ? syncMaps.repSlideOwnerCandidatesByMirrorKey[annualKey]
      : row.slideOwnerCandidates;
  const worksheetDraftRowKey = resolveWorksheetDraftStorageRowKey(
    annualKey,
    syncMirrorView,
    syncMaps.repAnnualCellKeyByMirrorKey
  );
  return {
    patchAnnualKey,
    draftLearningText: draftLt,
    cellLearningText: cellLt,
    indicatorLearningText,
    worksheetDraftRowKey,
    slideOwnerCandidates,
  };
}

export function resolveAnnualIndicatorLearningText(
  row: AnnualProgressLearningIndicatorRow,
  learningTextOverlayByAnnualKey: Record<string, string>,
  syncMaps: AnnualSyncMirrorIndicatorMaps = {
    repAnnualCellKeyByMirrorKey: {},
    repIndicatorCellsByMirrorKey: {},
    repSlideOwnerCandidatesByMirrorKey: {},
  }
): string {
  return resolveAnnualLearningIndicatorInputs(row, learningTextOverlayByAnnualKey, syncMaps).indicatorLearningText;
}

export function collectAnnualSlideOwnerSignatureKeys(
  rows: AnnualProgressLearningIndicatorRow[],
  learningTextOverlayByAnnualKey: Record<string, string>,
  syncMaps: AnnualSyncMirrorIndicatorMaps = {
    repAnnualCellKeyByMirrorKey: {},
    repIndicatorCellsByMirrorKey: {},
    repSlideOwnerCandidatesByMirrorKey: {},
  }
): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const row of rows) {
    const inputs = resolveAnnualLearningIndicatorInputs(row, learningTextOverlayByAnnualKey, syncMaps);
    const candidates = appendPresentationRefOwnerFromLearningText(
      [...inputs.slideOwnerCandidates],
      inputs.indicatorLearningText
    );
    for (const owner of candidates) {
      const key = programContentSlidePresenceKey(owner);
      if (seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
    }
  }
  keys.sort();
  return keys;
}

export function computeAnnualLearningSlidePagesByAnnualKey(params: {
  rows: AnnualProgressLearningIndicatorRow[];
  workspaceId: string;
  learningTextOverlayByAnnualKey: Record<string, string>;
  slidePresenceKeys: ReadonlySet<string>;
  slideDraftRev: number;
  deletedHints?: Record<string, { presentation?: boolean; worksheet?: boolean }>;
  syncMaps?: AnnualSyncMirrorIndicatorMaps;
}): Record<string, boolean> {
  void params.slideDraftRev;
  const syncMaps = params.syncMaps ?? {
    repAnnualCellKeyByMirrorKey: {},
    repIndicatorCellsByMirrorKey: {},
    repSlideOwnerCandidatesByMirrorKey: {},
  };
  const out: Record<string, boolean> = {};
  for (const row of params.rows) {
    const inputs = resolveAnnualLearningIndicatorInputs(row, params.learningTextOverlayByAnnualKey, syncMaps);
    out[row.annualCellKey] = resolvePresentationIndicatorVisible({
      workspaceId: params.workspaceId,
      annualKey: row.annualCellKey,
      patchAnnualKey: inputs.patchAnnualKey,
      scopeId: row.slideWorkspaceScopeId?.trim() || undefined,
      indicatorLearningText: inputs.indicatorLearningText,
      draftLearningText: inputs.draftLearningText,
      cellLearningText: inputs.cellLearningText,
      slideOwnerCandidates: inputs.slideOwnerCandidates,
      slidePresenceKeys: params.slidePresenceKeys,
      deletedHints: params.deletedHints,
    });
  }
  return out;
}

export function computeAnnualLearningMemoNoteByAnnualKey(params: {
  rows: AnnualProgressLearningIndicatorRow[];
  learningTextOverlayByAnnualKey: Record<string, string>;
  memoDraftRev: number;
  syncMaps?: AnnualSyncMirrorIndicatorMaps;
}): Record<string, boolean> {
  void params.memoDraftRev;
  const syncMaps = params.syncMaps ?? {
    repAnnualCellKeyByMirrorKey: {},
    repIndicatorCellsByMirrorKey: {},
    repSlideOwnerCandidatesByMirrorKey: {},
  };
  const out: Record<string, boolean> = {};
  for (const row of params.rows) {
    const inputs = resolveAnnualLearningIndicatorInputs(row, params.learningTextOverlayByAnnualKey, syncMaps);
    out[row.annualCellKey] = resolveLearningMemoNoteIndicatorVisible({
      indicatorLearningText: inputs.indicatorLearningText,
      draftLearningText: inputs.draftLearningText,
      cellLearningText: inputs.cellLearningText,
    });
  }
  return out;
}

export function computeAnnualLearningWorksheetByAnnualKey(params: {
  rows: AnnualProgressLearningIndicatorRow[];
  workspaceId: string;
  learningTextOverlayByAnnualKey: Record<string, string>;
  worksheetPresenceRefIds: ReadonlySet<string>;
  worksheetDraftRev: number;
  deletedHints?: Record<string, { presentation?: boolean; worksheet?: boolean }>;
  syncMaps?: AnnualSyncMirrorIndicatorMaps;
}): Record<string, boolean> {
  void params.worksheetDraftRev;
  const syncMaps = params.syncMaps ?? {
    repAnnualCellKeyByMirrorKey: {},
    repIndicatorCellsByMirrorKey: {},
    repSlideOwnerCandidatesByMirrorKey: {},
  };
  const out: Record<string, boolean> = {};
  for (const row of params.rows) {
    const inputs = resolveAnnualLearningIndicatorInputs(row, params.learningTextOverlayByAnnualKey, syncMaps);
    out[row.annualCellKey] = resolveWorksheetIndicatorVisible({
      workspaceId: params.workspaceId,
      annualKey: row.annualCellKey,
      patchAnnualKey: inputs.patchAnnualKey,
      worksheetDraftRowKey: inputs.worksheetDraftRowKey,
      indicatorLearningText: inputs.indicatorLearningText,
      draftLearningText: inputs.draftLearningText,
      cellLearningText: inputs.cellLearningText,
      worksheetPresenceRefIds: params.worksheetPresenceRefIds,
      deletedHints: params.deletedHints,
    });
  }
  return out;
}

export function computeProgramPresentationIndicatorVisible(params: {
  workspaceId: string;
  mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined;
  syncMirrorView: boolean;
  syncMirrorIndicatorCellsByAnnualKey: Record<string, ProgramProgressCell>;
  repDraftLearningText: string;
  slidePresenceKeys: ReadonlySet<string>;
  deletedHints?: Record<string, { presentation?: boolean; worksheet?: boolean }>;
  lsOverlayByAnnualKey?: Record<string, string>;
  /** 프로그램 표 workspace scopeId — 다른 표의 로컬 초안 잔상 차단 */
  scopeId?: string;
  /** 동기화 대표·미러 스코프 — 없으면 scopeId만 */
  allowedScopeIds?: readonly string[];
  ctx: ProgramLearningIndicatorRowContext;
}): boolean {
  const { draftLt, cellLt } = resolveIndicatorDraftAndCellTexts({
    annualKey: params.ctx.annualKey,
    patchAnnualKey: params.ctx.patchAnnualKey,
    draftLearningText: params.ctx.draftLearningText,
    cellLearningText: params.ctx.cellLearningText,
    lsOverlayByAnnualKey: params.lsOverlayByAnnualKey,
  });
  const indicatorLearningText =
    params.ctx.canonicalLearningText?.trim() ||
    resolveSyncMirrorLearningTextForIndicators(
      params.ctx.annualKey,
      params.syncMirrorView,
      params.syncMirrorIndicatorCellsByAnnualKey,
      draftLt,
      cellLt,
      params.repDraftLearningText
    );
  const slideOwnerCandidates = resolveSyncMirrorSlideOwnerCandidatesForIndicator({
    syncMirrorView: params.syncMirrorView,
    annualKey: params.ctx.annualKey,
    syncMirrorIndicatorCellsByAnnualKey: params.syncMirrorIndicatorCellsByAnnualKey,
    mode: params.mode,
    row: params.ctx.row,
    cell: params.ctx.cell,
    learningText: indicatorLearningText,
  });
  return resolvePresentationIndicatorVisible({
    workspaceId: params.workspaceId,
    annualKey: params.ctx.annualKey,
    patchAnnualKey: params.ctx.patchAnnualKey,
    scopeId: params.scopeId,
    allowedScopeIds: params.allowedScopeIds,
    indicatorLearningText,
    draftLearningText: draftLt,
    cellLearningText: cellLt,
    slideOwnerCandidates,
    slidePresenceKeys: params.slidePresenceKeys,
    deletedHints: params.deletedHints,
  });
}

export function computeProgramWorksheetIndicatorVisible(params: {
  workspaceId: string;
  syncMirrorView: boolean;
  syncMirrorRepAnnualCellKeyByMirrorKey: Record<string, string>;
  syncMirrorIndicatorCellsByAnnualKey: Record<string, ProgramProgressCell>;
  repDraftLearningText: string;
  worksheetPresenceRefIds: ReadonlySet<string>;
  deletedHints?: Record<string, { presentation?: boolean; worksheet?: boolean }>;
  lsOverlayByAnnualKey?: Record<string, string>;
  ctx: ProgramLearningIndicatorRowContext;
}): boolean {
  const patchAnnualKey =
    params.ctx.patchAnnualKey ??
    resolveWorksheetDraftStorageRowKey(
      params.ctx.annualKey,
      params.syncMirrorView,
      params.syncMirrorRepAnnualCellKeyByMirrorKey
    );
  const { draftLt, cellLt } = resolveIndicatorDraftAndCellTexts({
    annualKey: params.ctx.annualKey,
    patchAnnualKey,
    draftLearningText: params.ctx.draftLearningText,
    cellLearningText: params.ctx.cellLearningText,
    lsOverlayByAnnualKey: params.lsOverlayByAnnualKey,
  });
  const indicatorLearningText =
    params.ctx.canonicalLearningText?.trim() ||
    resolveSyncMirrorLearningTextForIndicators(
      params.ctx.annualKey,
      params.syncMirrorView,
      params.syncMirrorIndicatorCellsByAnnualKey,
      draftLt,
      cellLt,
      params.repDraftLearningText
    );
  const worksheetDraftRowKey = resolveWorksheetDraftStorageRowKey(
    params.ctx.annualKey,
    params.syncMirrorView,
    params.syncMirrorRepAnnualCellKeyByMirrorKey
  );
  const lsOverlayLearningText =
    params.lsOverlayByAnnualKey?.[patchAnnualKey] ?? params.lsOverlayByAnnualKey?.[params.ctx.annualKey];
  return resolveWorksheetIndicatorVisible({
    workspaceId: params.workspaceId,
    annualKey: params.ctx.annualKey,
    patchAnnualKey,
    worksheetDraftRowKey,
    indicatorLearningText,
    draftLearningText: draftLt,
    cellLearningText: cellLt,
    worksheetPresenceRefIds: params.worksheetPresenceRefIds,
    programCell: params.ctx.cell,
    syncMirrorRepCell: params.syncMirrorIndicatorCellsByAnnualKey[params.ctx.annualKey],
    lsOverlayLearningText,
    deletedHints: params.deletedHints,
  });
}
